import {
  ResizeableContent,
  RouteBreadcrumbs,
  ThemedPage,
  useApp,
  VolumeInventoryByTimeChart,
  VolumeInventoryChartForm,
  VolumeInventoryChartFormValues,
  VolumeInventorySearchToolbar
} from "@ots-frontend/ots-ui";
import {Card, Container, Grid, Group, LoadingOverlay, Menu, rem, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {
  adjustToTimeZone,
  cargoSubtype,
  containsValue,
  EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
  EntitySearchCriteria,
  getDefaultTimeFrame,
  transportMethod,
  TransportMethod,
  StatisticsGroupWrapper,
  StatisticsSearchCriteria,
  statisticsSelector,
  TimeGroup,
  timeGroup,
  useSearchStatisticsQuery,
  useSearchVolumeInventoryQuery,
  volumeInventoriesSelector,
  VolumeInventorySearchCriteria,
  volumeInventoryVolumeType,
  VolumeInventoryVolumeType
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useDispatch} from "react-redux";
import {getVolumeInventorySearchPageFilter} from "../../store/volume-inventory-search-page/selectors";
import {setVolumeInventorySearchPageFilter} from "../../store/volume-inventory-search-page/actions";

export const VolumeInventorySearchPage = () => {

  const dispatch = useDispatch()
  const {t} = useTranslation(["translation", "common"]);
  const {regionId, appCategory} = useApp()

  const filter = useAppSelector(getVolumeInventorySearchPageFilter)

  const [types, setTypes] = useState<VolumeInventoryVolumeType[]>([volumeInventoryVolumeType.usable, volumeInventoryVolumeType.unusable])
  const [haulMethods, setHaulMethods] = useState<TransportMethod[]>([transportMethod.trucking, transportMethod.pipeline])
  const [splitHaulMethods, setSplitHaulMethods] = useState<boolean | undefined>(true)
  const [splitTypes, setSplitTypes] = useState<boolean | undefined>(false)
  const [includeHauledVolume, setIncludeHauledVolume] = useState<boolean | undefined>(true)

  const selectVolumeInventories = useMemo(() => {
    return volumeInventoriesSelector()
  }, []);

  const volumeInventoryCriteria: VolumeInventorySearchCriteria | typeof skipToken = useMemo(() => {
    if (filter?.fromDttm && regionId) {
      const defaultTimeFrame = getDefaultTimeFrame(filter.fromDttm, filter.toDttm)

      let criteriaTimeGroup: TimeGroup
      if (defaultTimeFrame === "MONTH" || defaultTimeFrame === "WEEK" || defaultTimeFrame === "DAY") {
        criteriaTimeGroup = timeGroup.daily
      } else {
        criteriaTimeGroup = timeGroup.hourly
      }

      return {
        fromDttm: adjustToTimeZone(filter.fromDttm).getTime(),
        toDttm: filter.toDttm ? adjustToTimeZone(filter.toDttm).getTime() : undefined,
        timeGroup: criteriaTimeGroup.code,
        regionId: regionId,
        cargoSubtypes: [(appCategory?.cargoSubtype ?? cargoSubtype.water).code],
        entityCriteria: filter.entityCriteria,
      }
    } else {
      return skipToken
    }
  }, [filter?.fromDttm, filter?.toDttm, filter?.entityCriteria, regionId, appCategory?.cargoSubtype])

  const {snapshots, isLoading, refetch} = useSearchVolumeInventoryQuery(volumeInventoryCriteria, {
    selectFromResult: (result) => ({
      ...result,
      snapshots: selectVolumeInventories(result),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  const statsSearchCriteria: StatisticsSearchCriteria | typeof skipToken = useMemo(() => {
    if (volumeInventoryCriteria !== skipToken) {
      return {
        fromDttm: volumeInventoryCriteria.fromDttm,
        toDttm: volumeInventoryCriteria.toDttm,
        timeGroup: volumeInventoryCriteria.timeGroup,
        regionId: volumeInventoryCriteria.regionId ? [volumeInventoryCriteria.regionId] : undefined,
        categories: appCategory ? [appCategory.code] : undefined,
        includeTruckingData: true,
        includePipelineData: true,
        pipelineFlowTimeGroup: volumeInventoryCriteria.timeGroup,
        includeSubTruckCompanies: true,
      }
    } else {
      return skipToken
    }
  }, [volumeInventoryCriteria, appCategory])

  const selectStatistics = useMemo(() => {
    return statisticsSelector()
  }, []);

  const {statisticsBundle, isStatsLoading, refetch: refetchStats} = useSearchStatisticsQuery(statsSearchCriteria, {
    selectFromResult: (result) => ({
      statisticsBundle: selectStatistics(result, {
        ...result,
        ...EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
        processRegion: true,
        processTruckCompany: true,
        processRoute: true,
        processRouteGroup: true,
        processSite: true,
      }),
      isStatsLoading: result.isLoading || result.isFetching,
    })
  });

  const hauledStats: StatisticsGroupWrapper[] = useMemo(() => {
    const wrappers: StatisticsGroupWrapper[] = []

    if (includeHauledVolume) {
      if (filter?.entityCriteria && filter.entityCriteria.type) {
        const type = filter.entityCriteria.type

        if (filter.entityCriteria.ids && filter.entityCriteria.ids.length > 0) {
          for (const id of filter.entityCriteria.ids) {
            let wrapper: StatisticsGroupWrapper | undefined = undefined

            if (type === 'COMPANY') {
              wrapper = statisticsBundle.getTruckCompanyStats(id)
            } else if (type === 'SITE') {
              wrapper = statisticsBundle.getPickupSiteStats(id)
            } else if (type === 'ROUTE') {
              wrapper = statisticsBundle.getPickupRouteStats(id)
            } else if (type === 'ROUTEGROUP') {
              wrapper = statisticsBundle.getPickupRouteGroupStats(id)
            }

            if (wrapper) {
              wrappers.push(wrapper)
            }
          }
        } else {
          if (type === 'COMPANY') {
            wrappers.push(...statisticsBundle.truckCompanyStats)
          } else if (type === 'SITE') {
            wrappers.push(...statisticsBundle.pickupSiteStats)
          } else if (type === 'ROUTE') {
            wrappers.push(...statisticsBundle.pickupRouteStats)
          } else if (type === 'ROUTEGROUP') {
            wrappers.push(...statisticsBundle.pickupRouteGroupStats)
          }
        }
      } else if (regionId) {
        const wrapper = statisticsBundle.getRegionStats(regionId)
        if (wrapper) {
          wrappers.push(wrapper)
        }
      }
    }

    return wrappers
  }, [statisticsBundle, regionId, filter?.entityCriteria, includeHauledVolume])

  const onFilterChange = useCallback((value: {
    fromDate: Date,
    toDate: Date | undefined,
    entityType: string | undefined,
    entityIds: string[] | undefined
  }) => {
    let entityCriteria: EntitySearchCriteria | undefined = undefined
    if (value.entityType) {
      entityCriteria = {
        ids: value.entityIds,
        type: value.entityType
      }
    }

    dispatch(setVolumeInventorySearchPageFilter({
      fromDttm: value.fromDate.getTime(),
      toDttm: value.toDate?.getTime(),
      entityCriteria: entityCriteria
    }));
  }, [dispatch]);

  const onChartOptionsChange = useCallback((values: VolumeInventoryChartFormValues) => {
    const types: VolumeInventoryVolumeType[] = []

    if (values.includeUsableVolume) {
      types.push(volumeInventoryVolumeType.usable)
    }

    if (values.includeUnusableVolume) {
      types.push(volumeInventoryVolumeType.unusable)
    }

    if (values.includeSecondaryUsableVolume) {
      types.push(volumeInventoryVolumeType.secondaryUsable)
    }

    if (values.includeSecondaryUnusableVolume) {
      types.push(volumeInventoryVolumeType.secondaryUnusable)
    }

    const methods: TransportMethod[] = []

    if (values.includeTrucking) {
      methods.push(transportMethod.trucking)
    }

    if (values.includePipeline) {
      methods.push(transportMethod.pipeline)
    }

    setTypes(types)
    setSplitTypes(values.splitVolumes)
    setHaulMethods(methods)
    setSplitHaulMethods(values.splitHaulMethods)
    setIncludeHauledVolume(values.includeHauledVolume)
  }, [])

  const onRefreshClick = useCallback(() => {
    if (refetch) {
      refetch()
    }

    if (refetchStats) {
      refetchStats()
    }
  }, [refetch, refetchStats])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <RouteBreadcrumbs p="sm"/>
              <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                <Group position="apart">
                  <Title
                    order={4}>{t("page.volumeinventorysearch.title", "Volume Inventory", {ns: "translation"})}</Title>
                  <Menu
                    width={260}
                    position="bottom-end"
                    transitionProps={{transition: "pop-top-right"}}
                    withinPortal
                  >
                    <Menu.Target>
                      <UnstyledButton>
                        <Group spacing={7}>
                          <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                            Actions
                          </Text>
                          <IconChevronDown size={rem(12)} stroke={1.5}/>
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={onRefreshClick}>
                        {t("commons.action.refresh", "Refresh", {ns: "common"})}
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
                <VolumeInventorySearchToolbar
                  defaultValues={{
                    fromDate: filter?.fromDttm ? new Date(filter?.fromDttm) : undefined,
                    toDate: filter?.toDttm ? new Date(filter?.toDttm) : undefined,
                    entityType: filter?.entityCriteria?.type,
                    entityIds: filter?.entityCriteria?.ids,
                  }}
                  loading={isLoading || isStatsLoading}
                  onSubmit={onFilterChange}/>
              </Card>

              <LoadingOverlay visible={isLoading || isStatsLoading}/>

              <Container p="md" fluid={true}>
                <Grid>
                  <Grid.Col span={8}>
                    <Container fluid={true}>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <VolumeInventoryByTimeChart
                          id={"search_volume_inventory_by_time_chart"}
                          fromDttm={filter?.fromDttm ?? new Date().getTime()}
                          toDttm={filter?.toDttm}
                          snapshots={snapshots}
                          hauledStats={hauledStats}
                          category={appCategory}
                          types={types}
                          splitTypes={splitTypes}
                          methods={haulMethods}
                          splitMethods={splitHaulMethods}/>
                      </Card>
                    </Container>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                      <VolumeInventoryChartForm
                        defaultValues={{
                          includeUsableVolume: containsValue(types, volumeInventoryVolumeType.usable),
                          includeUnusableVolume: containsValue(types, volumeInventoryVolumeType.unusable),
                          includeSecondaryUsableVolume: containsValue(types, volumeInventoryVolumeType.secondaryUsable),
                          includeSecondaryUnusableVolume: containsValue(types, volumeInventoryVolumeType.secondaryUnusable),
                          includeHauledVolume: includeHauledVolume,
                          includeTrucking: containsValue(haulMethods, transportMethod.trucking),
                          includePipeline: containsValue(haulMethods, transportMethod.pipeline),
                          splitHaulMethods: splitHaulMethods,
                          splitVolumes: splitTypes,
                        }}
                        onSubmit={onChartOptionsChange}/>
                    </Card>
                  </Grid.Col>
                </Grid>
              </Container>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
